import React, { useState, useEffect } from "react";
import { X, Save, Loader2, Trash2, Upload, FileText } from "lucide-react";
import { toast } from "sonner";

const SUPER = (id) => `/api/vendors/admin/super/${id}`;

const FIELDS = [
  { k: "name", label: "Vendor name", group: "Contact & Service" },
  { k: "phone", label: "Phone", group: "Contact & Service" },
  { k: "alternate_phone", label: "Alternate phone", group: "Contact & Service" },
  { k: "email", label: "Email", group: "Contact & Service" },
  { k: "service_type", label: "Service type", group: "Contact & Service" },
  { k: "service_area", label: "Service area (comma separated)", group: "Contact & Service" },
  { k: "service_pincodes", label: "Serviced PIN codes (comma separated — used for mandatory matching)", group: "Contact & Service" },
  { k: "experience_years", label: "Experience (years)", group: "Contact & Service", type: "number" },
  { k: "pricing_range", label: "Pricing range", group: "Contact & Service" },
  { k: "bank_name", label: "Bank name", group: "Bank & Payment" },
  { k: "bank_account_holder", label: "A/C holder", group: "Bank & Payment" },
  { k: "bank_account_number", label: "A/C number", group: "Bank & Payment" },
  { k: "bank_ifsc", label: "IFSC", group: "Bank & Payment" },
  { k: "upi_id", label: "UPI ID", group: "Bank & Payment" },
  { k: "gst_number", label: "GST number", group: "Bank & Payment" },
  { k: "payment_cycle", label: "Payment cycle", group: "Bank & Payment" },
];

const VendorEditModal = ({ vendor, authAxios, docLabels = {}, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [docs, setDocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newDocType, setNewDocType] = useState("other");

  useEffect(() => {
    const init = {};
    FIELDS.forEach((f) => {
      const v = vendor[f.k];
      init[f.k] = (f.k === "service_area" || f.k === "service_pincodes")
        ? (Array.isArray(v) ? v.join(", ") : (v || ""))
        : (v ?? "");
    });
    init.about = vendor.about || "";
    const loc = vendor.shop_location || {};
    init.shop_address = loc.address || "";
    init.shop_location_url = loc.maps_url || "";
    setForm(init);
    setDocs(vendor.documents || []);
  }, [vendor]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (typeof payload.service_area === "string") {
        payload.service_area = payload.service_area.split(",").map((s) => s.trim()).filter(Boolean);
      }
      if (typeof payload.service_pincodes === "string") {
        payload.service_pincodes = payload.service_pincodes.split(",").map((s) => s.trim()).filter(Boolean);
      }
      if (payload.experience_years !== "" && payload.experience_years != null) {
        payload.experience_years = Number(payload.experience_years);
      } else {
        delete payload.experience_years;
      }
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      // Only re-resolve the Maps link when it actually changed (avoids re-geocoding on every save).
      const origUrl = (vendor.shop_location || {}).maps_url || "";
      if ((payload.shop_location_url || "") === origUrl) delete payload.shop_location_url;
      await authAxios.put(SUPER(vendor.id), payload);
      toast.success("Vendor details updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
    setSaving(false);
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await authAxios.delete(`${SUPER(vendor.id)}/document/${docId}`);
      setDocs((d) => d.filter((x) => x.id !== docId));
      toast.success("Document removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", newDocType);
      fd.append("doc_name", file.name);
      fd.append("file", file);
      const r = await authAxios.post(`${SUPER(vendor.id)}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((d) => [...d, { id: r.data.doc_id, type: newDocType, filename: file.name, verified: false }]);
      toast.success("Document uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const groups = ["Contact & Service", "Bank & Payment"];

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="vendor-edit-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold text-[#1E1B4B]">Edit Vendor — {vendor.name}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="vendor-edit-close"><X size={18} /></button>
        </div>
        <p className="text-xs text-[#94a3b8] mb-4">Super Admin only · changes apply immediately</p>

        {groups.map((g) => (
          <div key={g} className="mb-4">
            <h4 className="text-xs font-bold text-[#1E1B4B] uppercase tracking-wider mb-2">{g}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FIELDS.filter((f) => f.group === g).map((f) => (
                <div key={f.k}>
                  <label className="text-[11px] font-semibold text-[#64748B] block mb-1">{f.label}</label>
                  <input
                    type={f.type || "text"}
                    value={form[f.k] ?? ""}
                    onChange={(e) => set(f.k, e.target.value)}
                    data-testid={`vendor-edit-${f.k}`}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-[#E83E8C] outline-none"
                  />
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="mb-4">
          <label className="text-[11px] font-semibold text-[#64748B] block mb-1">About</label>
          <textarea value={form.about ?? ""} onChange={(e) => set("about", e.target.value)} rows={2}
            data-testid="vendor-edit-about"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-[#E83E8C] outline-none" />
        </div>

        {/* Shop Location */}
        <div className="mb-4">
          <h4 className="text-xs font-bold text-[#1E1B4B] uppercase tracking-wider mb-2">Shop Location</h4>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-[#64748B] block mb-1">Shop address</label>
              <textarea value={form.shop_address ?? ""} onChange={(e) => set("shop_address", e.target.value)} rows={2}
                data-testid="vendor-edit-shop_address"
                placeholder="Full shop address"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-[#E83E8C] outline-none" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-[#64748B] block mb-1">Google Maps location URL</label>
              <input
                type="text"
                value={form.shop_location_url ?? ""}
                onChange={(e) => set("shop_location_url", e.target.value)}
                data-testid="vendor-edit-shop_location_url"
                placeholder="Paste Google Maps link — we'll pin the shop from it"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-[#E83E8C] outline-none"
              />
              <p className="text-[11px] text-[#94a3b8] mt-1">
                {(() => {
                  const loc = vendor.shop_location || {};
                  return loc.lat && loc.lng
                    ? `Current pin: ${Number(loc.lat).toFixed(5)}, ${Number(loc.lng).toFixed(5)}`
                    : "No pin set yet. Paste a maps link to set one.";
                })()}
              </p>
            </div>
          </div>
        </div>

        {/* Documents */}
        <div className="mb-4">
          <h4 className="text-xs font-bold text-[#1E1B4B] uppercase tracking-wider mb-2">Documents</h4>
          <div className="space-y-2">
            {docs.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-xl" data-testid={`vendor-edit-doc-${doc.id}`}>
                <FileText size={15} className={doc.verified ? "text-green-500" : "text-amber-500"} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1E1B4B] truncate">{docLabels[doc.type] || doc.type}</p>
                  <p className="text-xs text-[#94a3b8] truncate">{doc.filename}</p>
                </div>
                <button onClick={() => deleteDoc(doc.id)} className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100" title="Remove" data-testid={`vendor-edit-doc-delete-${doc.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {docs.length === 0 && <p className="text-sm text-[#94a3b8]">No documents.</p>}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <select value={newDocType} onChange={(e) => setNewDocType(e.target.value)} className="px-2 py-2 text-sm border border-gray-200 rounded-xl" data-testid="vendor-edit-doctype">
              <option value="pan">PAN Card</option>
              <option value="aadhar">Aadhar Card</option>
              <option value="gst_certificate">GST Certificate</option>
              <option value="cancel_check">Cancelled Cheque</option>
              <option value="other">Other</option>
            </select>
            <label className="px-3 py-2 rounded-xl border border-dashed border-[#E83E8C]/40 text-sm font-semibold text-[#E83E8C] cursor-pointer flex items-center gap-2 hover:bg-[#FFF1F6]">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Add document
              <input type="file" accept=".jpg,.jpeg,.png,.pdf,.webp" className="hidden" data-testid="vendor-edit-doc-upload"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDoc(f); e.target.value = ""; }} />
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-200 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-xl bg-[#1E1B4B] text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-2" data-testid="vendor-edit-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorEditModal;
